import React, { useState, useEffect } from 'react';
import { contractsAPI, dashboardExtendedAPI, exportAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  FileText,
  Users,
  Calendar,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { trackRecentView } from '../hooks/useRecentlyViewed';
import { fmt, symbolFor } from '../lib/currency';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';

const getContractStatus = (startDate, endDate) => {
  const today = new Date();
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const warningDate = addDays(today, 30);

  if (isBefore(end, today)) {
    return { label: 'Expired', style: 'bg-red-500/15 text-red-400 border-red-500/20' };
  }
  if (isBefore(end, warningDate)) {
    return { label: 'Expiring Soon', style: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20' };
  }
  if (isAfter(start, today)) {
    return { label: 'Upcoming', style: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' };
  }
  return { label: 'Active', style: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20' };
};

export default function Contracts() {
  const { isAdmin, user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const sym = symbolFor(orgCurrency);
  const [contracts, setContracts] = useState([]);
  const [medicsPerContract, setMedicsPerContract] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    default_rate_payable: '',
    default_rate_chargeable: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, medicsRes] = await Promise.all([
        contractsAPI.list(),
        dashboardExtendedAPI.getMedicsPerContract(),
      ]);
      setContracts(contractsRes.data);
      setMedicsPerContract(medicsRes.data);
    } catch (error) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const getMedicCount = (contractId) => {
    const found = medicsPerContract.find(m => m.contract_id === contractId);
    return found?.medic_count || 0;
  };

  const handleCreate = async () => {
    try {
      await contractsAPI.create({
        ...formData,
        default_rate_payable: formData.default_rate_payable ? parseFloat(formData.default_rate_payable) : null,
        default_rate_chargeable: formData.default_rate_chargeable ? parseFloat(formData.default_rate_chargeable) : null,
      });
      toast.success('Contract created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    }
  };

  const handleEdit = async () => {
    if (!selectedContract) return;
    try {
      await contractsAPI.update(selectedContract.id, {
        ...formData,
        default_rate_payable: formData.default_rate_payable ? parseFloat(formData.default_rate_payable) : null,
        default_rate_chargeable: formData.default_rate_chargeable ? parseFloat(formData.default_rate_chargeable) : null,
      });
      toast.success('Contract updated successfully');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contract');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this contract?')) return;
    try {
      await contractsAPI.delete(id);
      toast.success('Contract deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contract');
    }
  };

  const openEditModal = (contract) => {
    setSelectedContract(contract);
    trackRecentView(user?.id, {
      type: 'contract',
      id: contract.id,
      label: contract.name,
      subtitle: contract.description || undefined,
      href: '/contracts',
    });
    setFormData({
      name: contract.name,
      description: contract.description || '',
      start_date: contract.start_date,
      end_date: contract.end_date,
      default_rate_payable: contract.default_rate_payable?.toString() || '',
      default_rate_chargeable: contract.default_rate_chargeable?.toString() || '',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      default_rate_payable: '',
      default_rate_chargeable: '',
    });
  };

  const handleCSVExport = async () => {
    setExportLoading(true);
    try {
      const baseUrl = exportAPI.getCSVUrl('contracts');
      const response = await fetch(baseUrl, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contracts_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const filteredContracts = contracts.filter(contract =>
    contract.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (contract.description && contract.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-1 gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#2B2B2B] border-white/10 text-[#F2F2F2]"
              data-testid="search-contracts-input"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCSVExport}
            disabled={exportLoading}
            className="border-[#1ABC9C] text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
            data-testid="export-contracts-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {isAdmin() && (
            <Button
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
              onClick={() => setShowCreateModal(true)}
              data-testid="add-contract-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contract
            </Button>
          )}
        </div>
      </div>

      {/* Contracts Table */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#A1A1AA]">Name</TableHead>
                <TableHead className="text-[#A1A1AA]">Duration</TableHead>
                <TableHead className="text-[#A1A1AA]">Status</TableHead>
                <TableHead className="text-[#A1A1AA]">Medics</TableHead>
                <TableHead className="text-[#A1A1AA]">Default Rates</TableHead>
                <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#A1A1AA] py-8">
                    No contracts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredContracts.map((contract) => {
                  const status = getContractStatus(contract.start_date, contract.end_date);
                  return (
                    <TableRow 
                      key={contract.id} 
                      className="border-white/5 hover:bg-white/5"
                      data-testid={`contract-row-${contract.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#1ABC9C]/20 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#1ABC9C]" />
                          </div>
                          <div>
                            <span className="font-medium text-[#F2F2F2]">{contract.name}</span>
                            {contract.description && (
                              <p className="text-xs text-[#A1A1AA] truncate max-w-[200px]">{contract.description}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <Calendar className="w-3 h-3" />
                          <span>{format(parseISO(contract.start_date), 'MMM dd, yyyy')}</span>
                          <span>-</span>
                          <span>{format(parseISO(contract.end_date), 'MMM dd, yyyy')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={status.style}>
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#A1A1AA]">
                          <Users className="w-4 h-4" />
                          <span>{getMedicCount(contract.id)} medics</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-1">
                            <span className="text-[#A1A1AA]">Pay:</span>
                            <span className="text-[#1ABC9C]">
                              {contract.default_rate_payable ? fmt(contract.default_rate_payable, orgCurrency) : '-'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[#A1A1AA]">Charge:</span>
                            <span className="text-[#F2F2F2]">
                              {contract.default_rate_chargeable ? fmt(contract.default_rate_chargeable, orgCurrency) : '-'}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {isAdmin() && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-[#F2F2F2]">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10">
                              <DropdownMenuItem onClick={() => openEditModal(contract)} className="text-[#F2F2F2] focus:bg-white/10">
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(contract.id)} className="text-red-400 focus:bg-white/10">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Add New Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contract Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., NHS Trust Agreement 2024"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="contract-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the contract..."
                className="bg-[#1A1A1A] border-white/10"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="contract-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="contract-end-date-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Rate Payable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_payable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_payable: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Rate Chargeable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_chargeable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_chargeable: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" data-testid="save-contract-btn">
              Create Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Edit Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contract Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Rate Payable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_payable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_payable: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Rate Chargeable ({sym})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_rate_chargeable}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_rate_chargeable: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleEdit} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
